import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExternalLinkAlt, faCoins, faSync } from '@fortawesome/free-solid-svg-icons';
import blockLinkUtils from '../utils/blocklink';
import { copyToClipboardWithToast } from '../utils/clipboard';
import './UniswapV2.css'; // Reusing same CSS for consistency
import { PeriodReturnChart, PnlSnapshot } from '../components/PeriodReturnChart';
import UniswapV4Reader, { TickArray } from '../utils/UniswapV4Reader';
import { CopyButton } from '../components/CopyButton';
import { LiquidityConcentrationChart } from '../components/LiquidityConcentrationChart';
import { ChainIcon } from '../components/ChainIcon';
import Web3 from '../utils/web3';

interface TokenDetail {
    address: string;
    symbol: string;
    decimals: number;
    reserve: string;
    usd_price: number;
}

interface PnlData {
    level: string;
    pnl_snapshots: PnlSnapshot[];
}

interface PoolDetail {
    pool_id: string;
    chain: string;
    protocol: string;
    tvl_usd: number;
    day_usd_ratio: number;
    week_usd_ratio: number;
    fee_day_ratio: number;
    fee_week_ratio: number;
    week_params: string;
    tokens: TokenDetail[];
    pnls?: PnlData[];
    pool_data?: any;
    updated_at: string;
}

interface ApiResponse {
    code: number;
    message: string;
    data: PoolDetail;
}

export function UniswapV4() {
    const { chain, pool_id } = useParams();
    const navigate = useNavigate();
    const [pool, setPool] = useState<PoolDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [tickArray, setTickArray] = useState<TickArray | null>(null);
    const [tickLoading, setTickLoading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const updatePoolInfo = async () => {
        if (!chain || !pool_id || !pool) return;
        setTickLoading(true);
        try {
            const tickArray = await UniswapV4Reader.uniswapV4Ticks(chain ?? '', pool_id ?? '', pool?.protocol ?? '', pool?.pool_data?.tick_spacing ?? 1);
            setTickArray(tickArray);
        } finally {
            setTickLoading(false);
        }
    }

    useEffect(() => {
        updatePoolInfo();
    }, [pool])

    useEffect(() => {
        if (!chain || !pool_id) return;

        setLoading(true);
        fetch(`/web3_liquidity/api/v1/liquidity_pool?chain=${chain}&pool_id=${pool_id}`)
            .then(res => res.json())
            .then((data: ApiResponse) => {
                if (data.code === 0) {
                    setPool(data.data);
                } else {
                    console.error("API Error:", data.message);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error("Fetch Error:", err);
                setLoading(false);
            });
    }, [chain, pool_id, refreshTrigger]);

    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
    };

    const formatNumber = (val: number, decimals: number = 4) => {
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: decimals }).format(val);
    };

    const formatPercent = (val: number) => {
        if (val === 0) return '-';
        return (val * 100 - 100).toFixed(4) + '%';
    };

    const formatFeePercent = (val: number) => {
        return (val * 100).toFixed(4) + '%';
    };

    const handleCopy = (text: string) => {
        copyToClipboardWithToast(text);
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
        toast.success('Refreshing data...', {
            style: {
                background: '#333',
                color: '#fff',
            },
            iconTheme: {
                primary: '#6366f1',
                secondary: '#333',
            },
        });
    };

    const getYieldClass = (val: number) => {
        if (val > 1) return 'yield-positive';
        if (val < 1) return 'yield-negative';
        return '';
    };

    const formatAddress = (address: string) => {
        if (address === '0x0000000000000000000000000000000000000000') return 'NATIVE';
        return address;
    };

    const getLiquidityLink = function (chain: string, protocol: string, address: string): string {
        if (!pool) return '';
        const tickSpace = pool?.pool_data?.tick_spacing
        const lowerTick = Web3.formatTick(JSON.parse(pool?.week_params).lower_tick, tickSpace)
        const upperTick = Web3.formatTick(JSON.parse(pool?.week_params).upper_tick, tickSpace)
        switch (protocol) {
            case 'UniswapV4':
                switch (chain) {
                    case 'bnb':
                        return `https://app.uniswap.org/positions/create/v4?currencyA=${formatAddress(pool?.tokens[0].address)}&currencyB=${formatAddress(pool?.tokens[1].address)}&chain=bnb&fee={%22feeAmount%22:${pool?.pool_data?.fee},%22tickSpacing%22:${tickSpace},%22isDynamic%22:false}&hook=${pool?.pool_data?.hooks}&priceRangeState={%22priceInverted%22:false,%22fullRange%22:false,%22minTick%22:${lowerTick},%22maxTick%22:${upperTick},%22initialPrice%22:%22%22,%22inputMode%22:%22price%22}&depositState={%22exactField%22:%22TOKEN0%22,%22exactAmounts%22:{}}&step=1`
                    case 'eth':
                        return `https://app.uniswap.org/positions/create/v4?currencyA=${formatAddress(pool?.tokens[0].address)}&currencyB=${formatAddress(pool?.tokens[1].address)}&chain=ethereum&fee={%22feeAmount%22:${pool?.pool_data?.fee},%22tickSpacing%22:${tickSpace},%22isDynamic%22:false}&hook=${pool?.pool_data?.hooks}&priceRangeState={%22priceInverted%22:false,%22fullRange%22:false,%22minTick%22:${lowerTick},%22maxTick%22:${upperTick},%22initialPrice%22:%22%22,%22inputMode%22:%22price%22}&depositState={%22exactField%22:%22TOKEN0%22,%22exactAmounts%22:{}}&step=1`
                    case 'base':
                        return `https://app.uniswap.org/positions/create/v4?currencyA=${formatAddress(pool?.tokens[0].address)}&currencyB=${formatAddress(pool?.tokens[1].address)}&chain=base&fee={%22feeAmount%22:${pool?.pool_data?.fee},%22tickSpacing%22:${tickSpace},%22isDynamic%22:false}&hook=${pool?.pool_data?.hooks}&priceRangeState={%22priceInverted%22:false,%22fullRange%22:false,%22minTick%22:${lowerTick},%22maxTick%22:${upperTick},%22initialPrice%22:%22%22,%22inputMode%22:%22price%22}&depositState={%22exactField%22:%22TOKEN0%22,%22exactAmounts%22:{}}&step=1`
                    case 'arbitrum':
                        return `https://app.uniswap.org/positions/create/v4?currencyA=${formatAddress(pool?.tokens[0].address)}&currencyB=${formatAddress(pool?.tokens[1].address)}&chain=arbitrum&fee={%22feeAmount%22:${pool?.pool_data?.fee},%22tickSpacing%22:${tickSpace},%22isDynamic%22:false}&hook=${pool?.pool_data?.hooks}&priceRangeState={%22priceInverted%22:false,%22fullRange%22:false,%22minTick%22:${lowerTick},%22maxTick%22:${upperTick},%22initialPrice%22:%22%22,%22inputMode%22:%22price%22}&depositState={%22exactField%22:%22TOKEN0%22,%22exactAmounts%22:{}}&step=1`
                    default:
                        throw new Error('Unsupported chain');
                }
            case 'PancakeInfCl':
                switch (chain) {
                    case 'bnb':
                        return `https://pancakeswap.finance/liquidity/add/bsc/infinity/${pool?.pool_id}?chain=bsc&persistChain=1&lowerTick=${lowerTick}&upperTick=${upperTick}`;
                    case 'base':
                        return `https://pancakeswap.finance/liquidity/add/base/infinity/${pool?.pool_id}?chain=base&persistChain=1&lowerTick=${lowerTick}&upperTick=${upperTick}`;
                    default:
                        throw new Error('Unsupported chain');
                }
            default:
                throw new Error('Unsupported protocol');
        }
    }

    if (loading || !pool) {
        return (
            <div className="uniswap-v2-page">
                <div className="loading-text">Loading Pool Data...</div>
            </div>
        );
    }

    const tokenTitle = pool.tokens.map(t => t.symbol).join(' / ');

    return (
        <div className="uniswap-v2-page">
            <div className="landing-background">
                <div className="orb orb-1"></div>
                <div className="orb orb-2"></div>
                <div className="grid-overlay"></div>
            </div>
            <header className="page-header">
                <button className="refresh-btn" onClick={handleRefresh} title="Refresh Data">
                    <FontAwesomeIcon icon={faSync} spin={loading} />
                </button>

                <div className="header-content">
                    <div className="pool-info">
                        <h1 className="pool-title">{tokenTitle}</h1>
                        <div className="pool-meta">
                            <ChainIcon chain={pool.chain} size={24} showLabel={false} />
                            <span className="protocol-badge">{pool.protocol}</span>
                        </div>
                    </div>

                    <div className="header-actions">
                        <a
                            href={getLiquidityLink(pool.chain, pool.protocol, pool.pool_id)}
                            target="_blank"
                            rel="noreferrer"
                            className="add-lp-btn"
                        >
                            <FontAwesomeIcon icon={faCoins} />
                            <span>Add Liquidity</span>
                        </a>
                    </div>
                </div>
            </header>

            <div className="grid-container">
                <div className="glass-card">
                    <h3 className="card-title">Pool Statistics</h3>
                    <div className="stat-row">
                        <span className="stat-label">TVL</span>
                        <span className="stat-value">{formatCurrency(pool.tvl_usd)}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">Protocol</span>
                        <span className="stat-value">{pool.protocol}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">Address</span>
                        <div className="stat-value pool-id-container">
                            <span
                                className="address-copy-trigger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(pool.pool_id);
                                }}
                                title="Click to copy address"
                            >
                                {pool.pool_id.substring(0, 6)}...{pool.pool_id.substring(pool.pool_id.length - 4)}
                            </span>
                            <a
                                href={blockLinkUtils.getProtocolLink(pool.chain, pool.protocol, pool.pool_id)}
                                target="_blank"
                                rel="noreferrer"
                                className="explorer-link"
                                style={{ width: '24px', height: '24px', borderRadius: '6px', fontSize: '0.8rem' }}
                            >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                            </a>
                        </div>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">Fee</span>
                        <span className="stat-value">{pool.pool_data?.fee ? (pool.pool_data.fee / 10000) + '%' : '0%'}</span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">24h USD</span>
                        <span className={`stat-value ${getYieldClass(pool.day_usd_ratio)}`}>
                            {formatPercent(pool.day_usd_ratio)}
                        </span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">7d USD</span>
                        <span className={`stat-value ${getYieldClass(pool.week_usd_ratio)}`}>
                            {formatPercent(pool.week_usd_ratio)}
                        </span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">24h Fee</span>
                        <span className={`stat-value ${getYieldClass(pool.fee_day_ratio + 1)}`}>
                            {formatFeePercent(pool.fee_day_ratio)}
                        </span>
                    </div>
                    <div className="stat-row">
                        <span className="stat-label">7d Fee</span>
                        <span className={`stat-value ${getYieldClass(pool.fee_week_ratio + 1)}`}>
                            {formatFeePercent(pool.fee_week_ratio)}
                        </span>
                    </div>
                </div>

                <div className="glass-card">
                    <h3 className="card-title">Token Composition</h3>
                    {pool.tokens.map(token => (
                        <div key={token.address} className="token-row">
                            <div className="token-info">
                                <span className="token-symbol">{token.symbol}</span>
                                <div className="pool-id-container">
                                    <span
                                        className="address-copy-trigger"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(token.address);
                                        }}
                                        title="Click to copy address"
                                    >
                                        {token.address.substring(0, 6)}...{token.address.substring(token.address.length - 4)}
                                    </span>
                                    <a
                                        href={blockLinkUtils.getAddressLink(pool.chain, token.address)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="explorer-link"
                                        style={{ width: '30px', height: '30px', borderRadius: '8px' }}
                                    >
                                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                                    </a>
                                </div>
                            </div>
                            <div className="token-reserves">
                                <span className="token-amount">
                                    {formatNumber(parseFloat(token.reserve) / Math.pow(10, token.decimals))}
                                </span>
                                <span className="token-usd">
                                    ${formatNumber(token.usd_price, 4)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="glass-card chart-section">
                <h3 className="card-title">Period Return Chart</h3>
                <PeriodReturnChart snapshots={pool.pnls?.[0]?.pnl_snapshots || []} />
            </div>

            <div className="glass-card chart-section" style={{ marginTop: '30px' }}>
                <h3 className="card-title">Liquidity Concentration (Normalized)</h3>
                <LiquidityConcentrationChart
                    symbol={`${pool.tokens[0].symbol}/${pool.tokens[1].symbol}`}
                    ticks={tickArray?.ticks || []}
                    price={tickArray?.price ?? 0}
                    decimals={pool.tokens[0].decimals - pool.tokens[1].decimals}
                    loading={tickLoading}
                />
            </div>
        </div>
    );
}

export default UniswapV4;
